import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AvatarDisplay } from '@/components/AvatarDisplay';
import { History, Trophy, Minus, Filter, X, Clock, User, Calendar as CalendarIcon, Download, FileText, FileCode, Loader2 } from 'lucide-react';
import { usePlayerProfiles } from '@/hooks/usePlayerProfiles';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { normalizeGameState } from '@/lib/gameStateUtils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import { Calendar } from '@/components/ui/calendar';

interface GameHistoryItem {
  id: string;
  white_player_id: string;
  black_player_id: string;
  winner: string | null;
  time_control: string | null;
  created_at: string;
  game_status: string;
}

interface GameHistorySectionProps {
  userId: string;
  maxGames?: number;
}

interface FilterState {
  result: 'all' | 'win' | 'loss' | 'draw';
  timeControl: string;
  opponentId: string | null;
  dateRange: {
    from: Date | undefined;
    to: Date | undefined;
  };
}

export function GameHistorySection({ userId, maxGames = 10 }: GameHistorySectionProps) {
  const [games, setGames] = useState<GameHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    result: 'all',
    timeControl: 'all',
    opponentId: null,
    dateRange: { from: undefined, to: undefined }
  });
  const navigate = useNavigate();
  const { toast } = useToast();

  // Get all opponent IDs
  const opponentIds = games.map(game => 
    game.white_player_id === userId ? game.black_player_id : game.white_player_id
  );
  const { profiles: opponentProfiles } = usePlayerProfiles(opponentIds);

  useEffect(() => {
    const fetchGameHistory = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('game_sessions')
        .select('id, white_player_id, black_player_id, winner, time_control, created_at, game_status')
        .or(`white_player_id.eq.${userId},black_player_id.eq.${userId}`)
        .in('game_status', ['checkmate', 'stalemate', 'resignation', 'timeout', 'draw'])
        .order('created_at', { ascending: false })
        .limit(maxGames);

      if (error) {
        console.error('Error fetching game history:', error);
      } else {
        setGames(data || []);
      }
      setLoading(false);
    };

    fetchGameHistory();
  }, [userId, maxGames]);

  // Get unique time controls from games
  const uniqueTimeControls = useMemo(() => {
    const controls = new Set(games.map(g => g.time_control).filter(Boolean));
    return Array.from(controls).sort();
  }, [games]);

  // Get unique opponents
  const uniqueOpponents = useMemo(() => {
    const opponentIdSet = new Set(
      games.map(game =>
        game.white_player_id === userId ? game.black_player_id : game.white_player_id
      )
    );
    return Array.from(opponentIdSet)
      .map(id => opponentProfiles[id])
      .filter(Boolean);
  }, [games, userId, opponentProfiles]);

  const getGameResult = (game: GameHistoryItem): 'win' | 'loss' | 'draw' => {
    if (!game.winner || game.winner === 'draw') return 'draw';
    
    const playerColor = game.white_player_id === userId ? 'white' : 'black';
    return game.winner === playerColor ? 'win' : 'loss';
  };

  // Filter games based on filter state
  const filteredGames = useMemo(() => {
    return games.filter(game => {
      // Filter by result
      if (filters.result !== 'all') {
        const result = getGameResult(game);
        if (result !== filters.result) return false;
      }

      // Filter by time control
      if (filters.timeControl !== 'all') {
        if (game.time_control !== filters.timeControl) return false;
      }

      // Filter by opponent
      if (filters.opponentId) {
        const opponentId = game.white_player_id === userId
          ? game.black_player_id
          : game.white_player_id;
        if (opponentId !== filters.opponentId) return false;
      }

      // Filter by date range
      if (filters.dateRange.from || filters.dateRange.to) {
        const gameDate = new Date(game.created_at);
        if (filters.dateRange.from && gameDate < filters.dateRange.from) return false;
        if (filters.dateRange.to && gameDate > filters.dateRange.to) return false;
      }

      return true;
    });
  }, [games, filters, userId]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.result !== 'all') count++;
    if (filters.timeControl !== 'all') count++;
    if (filters.opponentId) count++;
    if (filters.dateRange.from || filters.dateRange.to) count++;
    return count;
  }, [filters]);

  const clearAllFilters = () => {
    setFilters({
      result: 'all',
      timeControl: 'all',
      opponentId: null,
      dateRange: { from: undefined, to: undefined }
    });
  };

  // Helper: Download file
  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Helper: Format duration
  const formatDuration = (start: Date, end: Date): string => {
    const durationMs = end.getTime() - start.getTime();
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Helper: Format moves for PGN
  const formatMovesForPGN = (moveHistory: string[]): string => {
    if (!moveHistory || moveHistory.length === 0) return '';
    
    const movePairs: string[] = [];
    for (let i = 0; i < moveHistory.length; i += 2) {
      const moveNum = Math.floor(i / 2) + 1;
      const whiteMove = moveHistory[i];
      const blackMove = moveHistory[i + 1];
      
      if (blackMove) {
        movePairs.push(`${moveNum}. ${whiteMove} ${blackMove}`);
      } else {
        movePairs.push(`${moveNum}. ${whiteMove}`);
      }
    }
    
    return movePairs.join(' ');
  };

  // Export as CSV
  const exportAsCSV = async () => {
    try {
      setIsExporting(true);
      const gameIds = filteredGames.map(g => g.id);
      const { data: fullGames, error } = await supabase
        .from('game_sessions')
        .select('id, white_player_id, black_player_id, winner, time_control, created_at, ended_at, game_state, move_count')
        .in('id', gameIds);

      if (error) throw error;

      const headers = ['Date', 'Opponent', 'Result', 'Color', 'Time Control', 'Moves', 'Duration', 'Game ID'];
      const rows = (fullGames || []).map(game => {
        const opponentId = game.white_player_id === userId ? game.black_player_id : game.white_player_id;
        const opponent = opponentProfiles[opponentId];
        
        // Determine result
        let result: 'win' | 'loss' | 'draw' = 'draw';
        if (game.winner && game.winner !== 'draw') {
          const playerColor = game.white_player_id === userId ? 'white' : 'black';
          result = game.winner === playerColor ? 'win' : 'loss';
        }
        
        const playerColor = game.white_player_id === userId ? 'White' : 'Black';
        
        const duration = game.ended_at 
          ? formatDuration(new Date(game.created_at), new Date(game.ended_at))
          : 'N/A';
        
        return [
          format(new Date(game.created_at), 'yyyy-MM-dd HH:mm:ss'),
          opponent?.username || 'Unknown',
          result.charAt(0).toUpperCase() + result.slice(1),
          playerColor,
          game.time_control || 'N/A',
          game.move_count || 0,
          duration,
          game.id
        ];
      });

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      downloadFile(csvContent, `chesshatch-games-${format(new Date(), 'yyyy-MM-dd')}.csv`, 'text/csv');
      
      toast({
        title: "Export Successful",
        description: `Exported ${filteredGames.length} games as CSV`,
      });
    } catch (error) {
      console.error('CSV export error:', error);
      toast({
        title: "Export Failed",
        description: "Could not export game history",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Export as PGN
  const exportAsPGN = async () => {
    try {
      setIsExporting(true);
      const gameIds = filteredGames.map(g => g.id);
      const { data: fullGames, error } = await supabase
        .from('game_sessions')
        .select('id, white_player_id, black_player_id, winner, time_control, created_at, game_state, initial_fen')
        .in('id', gameIds);

      if (error) throw error;

      const pgnGames = (fullGames || []).map(game => {
        const whitePlayer = opponentProfiles[game.white_player_id];
        const blackPlayer = opponentProfiles[game.black_player_id];
        const gameState = game.game_state ? normalizeGameState(game.game_state) : null;
        const moveHistory = gameState?.moveHistory || [];
        
        let result = '1/2-1/2';
        if (game.winner === 'white') result = '1-0';
        if (game.winner === 'black') result = '0-1';
        
        const tags = [
          `[Event "ChessHatch Game"]`,
          `[Site "ChessHatch.com"]`,
          `[Date "${format(new Date(game.created_at), 'yyyy.MM.dd')}"]`,
          `[Round "1"]`,
          `[White "${whitePlayer?.username || 'Unknown'}"]`,
          `[Black "${blackPlayer?.username || 'Unknown'}"]`,
          `[Result "${result}"]`,
          game.time_control ? `[TimeControl "${game.time_control}"]` : '',
          game.initial_fen ? `[FEN "${game.initial_fen}"]` : '',
        ].filter(Boolean);
        
        const formattedMoves = formatMovesForPGN(moveHistory);
        
        return `${tags.join('\n')}\n\n${formattedMoves} ${result}\n`;
      });

      const pgnContent = pgnGames.join('\n\n');

      downloadFile(pgnContent, `chesshatch-games-${format(new Date(), 'yyyy-MM-dd')}.pgn`, 'application/x-chess-pgn');
      
      toast({
        title: "Export Successful",
        description: `Exported ${filteredGames.length} games as PGN`,
      });
    } catch (error) {
      console.error('PGN export error:', error);
      toast({
        title: "Export Failed",
        description: "Could not export game history",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const getResultBadge = (result: 'win' | 'loss' | 'draw') => {
    if (result === 'win') {
      return (
        <Badge className="bg-green-500/10 text-green-600 dark:bg-green-500/20 dark:text-green-400 hover:bg-green-500/20">
          <Trophy className="w-3 h-3 mr-1" />
          Win
        </Badge>
      );
    }
    if (result === 'loss') {
      return (
        <Badge variant="destructive" className="bg-red-500/10 text-red-600 dark:bg-red-500/20 dark:text-red-400 hover:bg-red-500/20">
          Loss
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="bg-muted/50">
        <Minus className="w-3 h-3 mr-1" />
        Draw
      </Badge>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Recent Games
          </CardTitle>
          <CardDescription>Loading game history...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (games.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Recent Games
          </CardTitle>
          <CardDescription>No games played yet</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <History className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>No game history available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="w-5 h-5" />
          Recent Games
        </CardTitle>
        <CardDescription>Last {games.length} completed games</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Filter Toggle & Summary */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {activeFilterCount > 0 && (
                <Badge className="ml-2" variant="secondary">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>

            {/* Export Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={filteredGames.length === 0 || isExporting}
                >
                  {isExporting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={exportAsCSV}>
                  <FileText className="w-4 h-4 mr-2" />
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportAsPGN}>
                  <FileCode className="w-4 h-4 mr-2" />
                  Export as PGN
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
            >
              <X className="w-4 h-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>

        {/* Filter Controls */}
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6 p-4 bg-muted/30 rounded-lg">
            {/* Result Filter */}
            <Select
              value={filters.result}
              onValueChange={(value: any) => setFilters(f => ({ ...f, result: value }))}
            >
              <SelectTrigger>
                <Trophy className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Result" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Results</SelectItem>
                <SelectItem value="win">Wins Only</SelectItem>
                <SelectItem value="loss">Losses Only</SelectItem>
                <SelectItem value="draw">Draws Only</SelectItem>
              </SelectContent>
            </Select>

            {/* Time Control Filter */}
            <Select
              value={filters.timeControl}
              onValueChange={(value) => setFilters(f => ({ ...f, timeControl: value }))}
            >
              <SelectTrigger>
                <Clock className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Time Control" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time Controls</SelectItem>
                {uniqueTimeControls.map(tc => (
                  <SelectItem key={tc} value={tc}>{tc}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Opponent Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start">
                  <User className="w-4 h-4 mr-2" />
                  <span className="truncate">
                    {filters.opponentId
                      ? opponentProfiles[filters.opponentId]?.username || 'Selected'
                      : "All Opponents"}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[200px] p-0">
                <Command>
                  <CommandInput placeholder="Search opponent..." />
                  <CommandEmpty>No opponent found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem onSelect={() => setFilters(f => ({ ...f, opponentId: null }))}>
                      All Opponents
                    </CommandItem>
                    {uniqueOpponents.map(opponent => (
                      <CommandItem
                        key={opponent.id}
                        onSelect={() => setFilters(f => ({ ...f, opponentId: opponent.id }))}
                      >
                        {opponent.username || opponent.display_name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>

            {/* Date Range Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  <span className="truncate">
                    {filters.dateRange.from || filters.dateRange.to
                      ? `${filters.dateRange.from ? format(filters.dateRange.from, 'PP') : '...'} - ${filters.dateRange.to ? format(filters.dateRange.to, 'PP') : '...'}`
                      : "All Dates"}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <div className="p-3 space-y-2">
                  <div>
                    <label className="text-xs font-medium mb-1 block">From:</label>
                    <Calendar
                      mode="single"
                      selected={filters.dateRange.from}
                      onSelect={(date) => setFilters(f => ({ ...f, dateRange: { ...f.dateRange, from: date } }))}
                      initialFocus
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1 block">To:</label>
                    <Calendar
                      mode="single"
                      selected={filters.dateRange.to}
                      onSelect={(date) => setFilters(f => ({ ...f, dateRange: { ...f.dateRange, to: date } }))}
                    />
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        )}

        {/* Filtered Results Info */}
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-muted-foreground">
            Showing {filteredGames.length} of {games.length} games
          </p>
        </div>

        {/* Games List */}
        <div className="space-y-3">
          {filteredGames.map((game) => {
            const opponentId = game.white_player_id === userId ? game.black_player_id : game.white_player_id;
            const opponent = opponentProfiles[opponentId];
            const result = getGameResult(game);
            const playerColor = game.white_player_id === userId ? 'white' : 'black';

            return (
              <div
                key={game.id}
                className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
                onClick={() => opponent && navigate(`/player/${opponent.id}`)}
              >
                {/* Result Badge */}
                <div className="flex-shrink-0">
                  {getResultBadge(result)}
                </div>

                {/* Opponent Info */}
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {opponent && (
                    <>
                      <AvatarDisplay
                        username={opponent.username}
                        avatarConfig={opponent.avatar_config}
                        avatarUrl={opponent.avatar_url}
                        size="sm"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          vs. {opponent.display_name || opponent.username || 'Anonymous'}
                        </p>
                        {opponent.username && (
                          <p className="text-xs text-muted-foreground truncate">
                            @{opponent.username} • {opponent.elo_rating}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                  {!opponent && (
                    <p className="text-sm text-muted-foreground">vs. Unknown Player</p>
                  )}
                </div>

                {/* Game Details */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge variant="outline" className="text-xs">
                    {playerColor === 'white' ? '⚪' : '⚫'}
                  </Badge>
                  {game.time_control && (
                    <Badge variant="outline" className="text-xs">
                      {game.time_control}
                    </Badge>
                  )}
                  <span className="text-xs text-muted-foreground hidden sm:inline">
                    {formatDistanceToNow(new Date(game.created_at), { addSuffix: true })}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State for Filtered Results */}
        {filteredGames.length === 0 && games.length > 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Filter className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>No games match your filters</p>
            <Button
              variant="link"
              size="sm"
              onClick={clearAllFilters}
              className="mt-2"
            >
              Clear all filters
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
